import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { 
  AlertTriangle, Search, Loader2, Eye, Clock, User, MapPin, Camera,
  ChevronRight, X, Filter, AlertCircle, CheckCircle2, Search as SearchIcon,
  MoreHorizontal, Trash2, MessageSquare
} from 'lucide-react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useToast } from '../../hooks/use-toast';

const SEVERITY_CONFIG = {
  low: { 
    color: 'bg-green-100 text-green-700 border-green-200',
    dotColor: 'bg-green-500',
    label: 'Low'
  },
  medium: { 
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    dotColor: 'bg-amber-500',
    label: 'Medium'
  },
  high: { 
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    dotColor: 'bg-orange-500',
    label: 'High'
  },
  critical: { 
    color: 'bg-red-100 text-red-700 border-red-200',
    dotColor: 'bg-red-500',
    label: 'Critical'
  },
};

const STATUS_CONFIG = {
  open: { 
    color: 'bg-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    label: 'Open',
    icon: AlertCircle
  },
  investigating: { 
    color: 'bg-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    label: 'Investigating',
    icon: SearchIcon
  },
  resolved: { 
    color: 'bg-green-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    label: 'Resolved',
    icon: CheckCircle2
  },
};

// Status Toggle Pills
const StatusToggle = ({ currentStatus, onChange, disabled }) => {
  const statuses = ['open', 'investigating', 'resolved'];
  
  return (
    <div className="inline-flex bg-slate-100 rounded-full p-1">
      {statuses.map((status) => {
        const config = STATUS_CONFIG[status];
        const isActive = currentStatus === status;
        const Icon = config.icon;
        
        return (
          <button
            key={status}
            onClick={() => onChange(status)}
            disabled={disabled || isActive}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              isActive 
                ? `${config.bgColor} text-slate-800 shadow-sm` 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className={`w-3.5 h-3.5 ${isActive ? config.color.replace('bg-', 'text-') : ''}`} />
            {config.label}
          </button>
        );
      })}
    </div>
  );
};

// Incident Card Component
const IncidentCard = ({ incident, onClick, onStatusChange }) => {
  const severity = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.medium;
  const status = STATUS_CONFIG[incident.status] || STATUS_CONFIG.open;
  const StatusIcon = status.icon;
  
  return (
    <div 
      className={`group p-4 rounded-xl border-2 transition-all hover:shadow-lg cursor-pointer ${status.bgColor} ${status.borderColor}`}
      onClick={onClick}
      data-testid={`incident-card-${incident.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${severity.dotColor}`}></div>
          <Badge variant="outline" className={`text-[10px] ${severity.color}`}>
            {severity.label}
          </Badge>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStatusChange(incident.id, 'open'); }}>
                <AlertCircle className="w-4 h-4 mr-2 text-amber-500" /> Mark Open
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStatusChange(incident.id, 'investigating'); }}>
                <SearchIcon className="w-4 h-4 mr-2 text-blue-500" /> Mark Investigating
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStatusChange(incident.id, 'resolved'); }}>
                <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> Mark Resolved
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Title & Description */}
      <h3 className="font-semibold text-slate-800 mb-1 line-clamp-1">{incident.title}</h3>
      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{incident.description}</p>
      
      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-1">
          <User className="w-3 h-3" />
          {incident.reporter_name}
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {format(new Date(incident.created_at), 'MMM d, h:mm a')}
        </div>
      </div>
      
      {/* Photos indicator */}
      {incident.photos?.length > 0 && (
        <div className="mt-2 flex items-center gap-1 text-xs text-slate-400">
          <Camera className="w-3 h-3" />
          {incident.photos.length} photo{incident.photos.length > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

// Kanban Column
const KanbanColumn = ({ status, incidents, onIncidentClick, onStatusChange }) => {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  
  return (
    <div className="flex-1 min-w-[320px]">
      {/* Column Header */}
      <div className={`flex items-center justify-between p-3 rounded-t-xl ${config.bgColor} border-b-2 ${config.borderColor}`}>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">{config.label}</h3>
            <p className="text-xs text-slate-500">{incidents.length} incident{incidents.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>
      
      {/* Column Content */}
      <ScrollArea className={`h-[calc(100vh-380px)] min-h-[400px] rounded-b-xl border-x-2 border-b-2 ${config.borderColor} bg-slate-50/50`}>
        <div className="p-3 space-y-3">
          {incidents.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <Icon className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No incidents</p>
            </div>
          ) : (
            incidents.map((incident) => (
              <IncidentCard
                key={incident.id}
                incident={incident}
                onClick={() => onIncidentClick(incident)}
                onStatusChange={onStatusChange}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default function AdminIncidents() {
  const { toast } = useToast();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [viewMode, setViewMode] = useState('kanban'); // kanban or list

  const loadIncidents = async () => {
    try {
      const data = await api.getIncidents();
      setIncidents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading incidents:', error);
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIncidents();
  }, []);

  // Filtered incidents
  const filteredIncidents = useMemo(() => {
    return incidents.filter(incident => {
      const matchesSearch = incident.title?.toLowerCase().includes(search.toLowerCase()) ||
        incident.description?.toLowerCase().includes(search.toLowerCase()) ||
        incident.reporter_name?.toLowerCase().includes(search.toLowerCase());
      const matchesSeverity = severityFilter === 'all' || incident.severity === severityFilter;
      return matchesSearch && matchesSeverity;
    });
  }, [incidents, search, severityFilter]);

  // Group by status for kanban
  const groupedIncidents = useMemo(() => ({
    open: filteredIncidents.filter(i => i.status === 'open'),
    investigating: filteredIncidents.filter(i => i.status === 'investigating'),
    resolved: filteredIncidents.filter(i => i.status === 'resolved'),
  }), [filteredIncidents]);

  // Stats
  const stats = useMemo(() => ({
    total: incidents.length,
    critical: incidents.filter(i => i.severity === 'critical' && i.status !== 'resolved').length,
    open: incidents.filter(i => i.status === 'open').length,
    resolved: incidents.filter(i => i.status === 'resolved').length,
  }), [incidents]);

  const handleStatusChange = async (incidentId, newStatus) => {
    setUpdating(true);
    try {
      await api.updateIncident(incidentId, { status: newStatus });
      toast({
        title: "Status Updated",
        description: `Incident marked as ${newStatus}`,
      });
      loadIncidents();
      if (selectedIncident?.id === incidentId) {
        setSelectedIncident(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to update status',
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
            <p className="mt-3 text-slate-500">Loading incidents...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-incidents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Incidents</h1>
            <p className="text-slate-500 text-sm">Track and manage incident reports</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'kanban' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
                }`}
              >
                Board
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'list' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'
                }`}
              >
                List
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700">{stats.critical}</p>
                  <p className="text-xs text-red-600">Critical Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{stats.open}</p>
                  <p className="text-xs text-amber-600">Open</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{stats.resolved}</p>
                  <p className="text-xs text-green-600">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search incidents..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-10"
                />
                {search && (
                  <button 
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Severity Filter Pills */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSeverityFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    severityFilter === 'all' 
                      ? 'bg-slate-800 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  All
                </button>
                {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => setSeverityFilter(key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                      severityFilter === key 
                        ? config.color
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${config.dotColor}`}></div>
                    {config.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Kanban View */}
        {viewMode === 'kanban' && (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {['open', 'investigating', 'resolved'].map((status) => (
              <KanbanColumn
                key={status}
                status={status}
                incidents={groupedIncidents[status]}
                onIncidentClick={setSelectedIncident}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {filteredIncidents.length === 0 ? (
                <div className="p-12 text-center">
                  <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                  <p className="text-lg font-medium text-slate-600">No incidents found</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {search ? 'Try a different search term' : 'All clear!'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredIncidents.map((incident) => {
                    const severity = SEVERITY_CONFIG[incident.severity];
                    return (
                      <div 
                        key={incident.id}
                        onClick={() => setSelectedIncident(incident)}
                        className="p-4 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-4"
                      >
                        <div className={`w-3 h-3 rounded-full ${severity.dotColor}`}></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{incident.title}</p>
                          <p className="text-sm text-slate-500 truncate">{incident.description}</p>
                        </div>
                        <Badge variant="outline" className={severity.color}>
                          {severity.label}
                        </Badge>
                        <StatusToggle 
                          currentStatus={incident.status}
                          onChange={(status) => handleStatusChange(incident.id, status)}
                          disabled={updating}
                        />
                        <div className="text-sm text-slate-400 hidden md:block">
                          {format(new Date(incident.created_at), 'MMM d')}
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Incident Detail Sheet */}
      <Sheet open={!!selectedIncident} onOpenChange={(open) => !open && setSelectedIncident(null)}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className={SEVERITY_CONFIG[selectedIncident?.severity]?.color}>
                {SEVERITY_CONFIG[selectedIncident?.severity]?.label}
              </Badge>
            </div>
            <SheetTitle className="text-xl">{selectedIncident?.title}</SheetTitle>
            <SheetDescription className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" /> {selectedIncident?.reporter_name}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" /> {selectedIncident?.created_at && format(new Date(selectedIncident.created_at), 'MMM d, yyyy h:mm a')}
              </span>
            </SheetDescription>
          </SheetHeader>
          
          <div className="space-y-6 mt-6">
            {/* Status Toggle */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Status</label>
              <StatusToggle
                currentStatus={selectedIncident?.status}
                onChange={(status) => handleStatusChange(selectedIncident.id, status)}
                disabled={updating}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Description</label>
              <div className="p-4 bg-slate-50 rounded-xl text-slate-700">
                {selectedIncident?.description}
              </div>
            </div>

            {/* Location */}
            {selectedIncident?.location_description && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Location
                </label>
                <div className="p-3 bg-slate-50 rounded-lg text-slate-600 text-sm">
                  {selectedIncident.location_description}
                </div>
              </div>
            )}

            {/* Photos */}
            {selectedIncident?.photos?.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Photos ({selectedIncident.photos.length})
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {selectedIncident.photos.map((photo, i) => (
                    <img 
                      key={i} 
                      src={photo} 
                      alt={`Evidence ${i + 1}`} 
                      className="rounded-xl w-full h-32 object-cover border border-slate-200" 
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Site */}
            {selectedIncident?.site_name && (
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <MapPin className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">{selectedIncident.site_name}</span>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
